import { createSlice } from '@reduxjs/toolkit'
import { ISeat, ITab, ITabDetail, ITabItem, IOrderDetail } from 'interfaces/tab'

const tabSlice = createSlice({
  name: 'tab',
  initialState: {
    tabData: {},
    tabDetailData: null,
    tabItems: [],
    seats: [],
    subtotal: 0,
    tax: 0,
    isPartialPaidFromPos: false,
    isLoading: false,
  },
  reducers: {
    setTabLoading: (state, action) => {
      state.isLoading = action.payload
    },
    setTab: (state, action) => {
      state.tabData = action.payload
    },
    setTabDetail: (state, action) => {
      // Preserve existing selection state if this is an update
      const existingSelectionState = new Map()
      if (state.tabDetailData?.orders) {
        state.tabDetailData.orders.forEach((order: IOrderDetail) => {
          existingSelectionState.set(order.orderId, {
            orderSelected: order.isSelected,
            itemSelections: new Map(
              order.items?.map((item) => [item.itemId, item.isSelected]) || []
            ),
          })
        })
      }

      state.tabDetailData = action.payload
      state.isLoading = false // Set loading to false when data is received

      // Set selection state - preserve existing selections or use defaults for new orders
      if (action.payload?.orders) {
        action.payload.orders.forEach((order: IOrderDetail) => {
          const hasUnpaidItems =
            order.items?.some((item: any) => !item.isPaid) ?? false
          const existingOrderState = existingSelectionState.get(order.orderId)

          if (existingOrderState) {
            // Preserve existing selection state
            order.isSelected = existingOrderState.orderSelected
            if (order.items) {
              order.items.forEach((item: any) => {
                const existingItemSelection =
                  existingOrderState.itemSelections.get(item.itemId)
                if (existingItemSelection !== undefined) {
                  item.isSelected = existingItemSelection
                } else {
                  // New item - select if unpaid
                  item.isSelected = !item.isPaid
                }
              })
            }
          } else {
            // New order - use default logic
            // eslint-disable-next-line no-lonely-if
            if (hasUnpaidItems) {
              order.isSelected = true
              if (order.items) {
                order.items.forEach((item: any) => {
                  item.isSelected = !item.isPaid // Select only unpaid items
                })
              }
            } else {
              order.isSelected = false
              if (order.items) {
                order.items.forEach((item: any) => {
                  item.isSelected = false
                })
              }
            }
          }
        })
      }

      state.subtotal = action?.payload?.orders?.reduce(
        (prev: number, next: IOrderDetail) => {
          return prev + next.subTotal
        },
        0
      )
      state.tax = action?.payload?.orders.reduce(
        (prev: number, next: IOrderDetail) => {
          return prev + next.tax
        },
        0
      )
    },
    setTabSubtotal: (state, action) => {
      state.subtotal = action.payload
    },
    setTabTax: (state, action) => {
      state.tax = action.payload
    },
    setTabItems: (state, action) => {
      const items: ITabItem[] = action.payload
      const newData = items.map((item) => {
        return { ...item, isSelected: false }
      })
      state.tabItems = newData
    },
    setPosPay: (state, action) => {
      const newData: boolean = action.payload
      state.isPartialPaidFromPos = newData
    },
    updateItems: (state, action) => {
      const { item, isChecked } = action.payload
      const itemIndex = state.tabItems.findIndex(
        (d) => d.posItemId === item.posItemId && d.itemId === item.itemId
      )
      state.tabItems[itemIndex] = { ...item, isSelected: isChecked }
      state.tabItems = [...state.tabItems]
    },
    updateOrderItems: (state, action) => {
      const { orderId, isChecked } = action.payload
      if (orderId === null) {
        state?.tabDetailData?.orders?.forEach((ele) => {
          ele.items.forEach((element) => {
            element.isSelected = false
            state.tabItems.forEach((index) => {
              if (index.itemId === element.itemId) {
                index.isSelected = false
              }
            })
          })
        })
      } else {
        const itemIndex = state.tabDetailData.orders.findIndex(
          (d) => d.orderId === orderId
        )

        // Check if order has any unpaid items
        const order = state.tabDetailData.orders[itemIndex]
        const hasUnpaidItems =
          order.items?.some((item) => !item.isPaid) ?? false
        if (!hasUnpaidItems) {
          return // Don't update selection for orders with no unpaid items
        }

        state.tabDetailData.orders[itemIndex].isSelected = isChecked
        state.tabDetailData.orders[itemIndex].items.forEach((element) => {
          // Only select unpaid items
          element.isSelected = isChecked && !element.isPaid
          state.tabItems.forEach((index) => {
            if (index.itemId === element.itemId) {
              index.isSelected = isChecked && !element.isPaid
            }
          })
        })
      }
      state.tabDetailData.orders = [...state.tabDetailData.orders]
    },
    setTabSeats: (state, action) => {
      state.seats = action.payload
    },
    updateSeats: (state, action) => {
      const { seat, isChecked } = action.payload
      const seatIndex = state.seats.findIndex(
        (d) => d.seatNumber === seat.seatNumber
      )
      state.seats[seatIndex] = { ...seat, isSelected: isChecked }
      state.seats = [...state.seats]
    },
    setUpdatedItems: (state, action) => {
      state.tabItems.forEach((updateItem) => {
        action.payload.forEach((currentItem) => {
          if (currentItem.itemId === updateItem.itemId) {
            if (currentItem.isPaid) {
              updateItem.isSelected = false
            }
          }
        })
      })
      state.tabItems = [...action.payload]
    },
    updateOrderStatusFromSignalR: (state, action) => {
      const { orderId, status } = action.payload

      // Update order status in the tab detail
      if (state.tabDetailData?.orders) {
        const order = state.tabDetailData.orders.find(
          (o) => o.orderId === orderId
        )
        if (order) {
          order.status = status
          order.isSelected =
            status === 'paid' || status === 'processing'
              ? false
              : order.isSelected

          // Update items in the order
          if (order.items) {
            order.items.forEach((item) => {
              if (status === 'paid' || status === 'processing') {
                item.isSelected = false
                item.isPaid = status === 'paid'
              }
            })
          }
        }

        // Check if all orders are now paid and set tabId to 'closed' if so
        const allOrdersPaid = state.tabDetailData.orders.every(
          (orders) => orders.status === 'paid'
        )
        if (allOrdersPaid && state.tabDetailData.orders.length > 0) {
          sessionStorage.setItem('tabId', 'closed')
          console.log('🔒 All orders paid - Tab ID set to closed via SignalR')
        }
      }

      // Update items in tabItems array
      if (state.tabItems) {
        state.tabItems.forEach((item) => {
          if (item.orderId === orderId) {
            if (status === 'paid' || status === 'processing') {
              item.isSelected = false
              item.isPaid = status === 'paid'
            }
          }
        })
      }
    },
    updateMultipleOrderStatusesFromSignalR: (state, action) => {
      action.payload.forEach(({ orderId, status }) => {
        // Update order status in the tab detail
        if (state.tabDetailData?.orders) {
          const order = state.tabDetailData.orders.find(
            (o) => o.orderId === orderId
          )
          if (order) {
            order.status = status
            order.isSelected =
              status === 'paid' || status === 'processing'
                ? false
                : order.isSelected

            // Update items in the order
            if (order.items) {
              order.items.forEach((item) => {
                if (status === 'paid' || status === 'processing') {
                  item.isSelected = false
                  item.isPaid = status === 'paid'
                }
              })
            }
          }
        }

        // Update items in tabItems array
        if (state.tabItems) {
          state.tabItems.forEach((item) => {
            if (item.orderId === orderId) {
              if (status === 'paid' || status === 'processing') {
                item.isSelected = false
                item.isPaid = status === 'paid'
              }
            }
          })
        }
      })

      // Check if all orders are now paid and set tabId to 'closed' if so
      if (state.tabDetailData?.orders) {
        const allOrdersPaid = state.tabDetailData.orders.every(
          (order) => order.status === 'paid'
        )
        if (allOrdersPaid && state.tabDetailData.orders.length > 0) {
          sessionStorage.setItem('tabId', 'closed')
          console.log(
            '🔒 All orders paid - Tab ID set to closed via SignalR (multiple updates)'
          )
        }
      }
    },
    closeSelectedOrders: (state) => {
      // Set isOpen to false for all selected orders
      if (state.tabDetailData?.orders) {
        state.tabDetailData.orders.forEach((order) => {
          if (order.isSelected) {
            order.isOpen = false
          }
        })
      }
    },
    reselectAllOrders: (state) => {
      // Re-select all unpaid orders
      if (state.tabDetailData?.orders) {
        state.tabDetailData.orders.forEach((order) => {
          // Only select orders that are not paid, not processing, and are open
          if (
            order.status !== 'paid' &&
            order.status !== 'processing' &&
            order.isOpen
          ) {
            order.isSelected = true
            // Also select all items in the order
            if (order.items) {
              order.items.forEach((item) => {
                item.isSelected = true
              })
            }
          }
        })
      }

      // Update tabItems array as well
      if (state.tabItems) {
        state.tabItems.forEach((item) => {
          // Only select items that are not paid and belong to open orders
          if (!item.isPaid && item.isOpen) {
            item.isSelected = true
          }
        })
      }
    },
  },
})

export const {
  setTab,
  setTabDetail,
  setTabSubtotal,
  setTabTax,
  setTabItems,
  updateItems,
  setTabSeats,
  updateSeats,
  setPosPay,
  updateOrderItems,
  setUpdatedItems,
  updateOrderStatusFromSignalR,
  updateMultipleOrderStatusesFromSignalR,
  closeSelectedOrders,
  reselectAllOrders,
  setTabLoading,
} = tabSlice.actions

export default tabSlice.reducer

export const tabData = (state: { tab: { tabData: ITab } }) => state.tab.tabData
export const tabDetail = (state: { tab: { tabDetailData: ITabDetail } }) =>
  state.tab.tabDetailData
export const tabSubtotal = (state: { tab: { subtotal: number } }) =>
  state.tab.subtotal
export const tabTax = (state: { tab: { tax: number } }) => state.tab.tax
export const tabItems = (state: { tab: { tabItems: ITabItem[] } }) =>
  state.tab.tabItems
export const seats = (state: { tab: { seats: ISeat[] } }) => state.tab.seats
export const isPartialPaidFromPos = (state: {
  tab: { isPartialPaidFromPos: boolean }
}) => state.tab.isPartialPaidFromPos
export const tabIsLoading = (state: { tab: { isLoading: boolean } }) =>
  state.tab.isLoading
